// configurator management data hooks (admin catalogue editing).

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminCatalogueResponse,
  CatalogueCategoryInput,
  CatalogueItemInput,
  CatalogueModelInput,
} from '@technohull/shared';
import { api } from '../lib/api.js';

export function useAdminCatalogue(modelId: number | null) {
  return useQuery({
    queryKey: ['admin-catalogue', modelId ?? 'default'],
    queryFn: () => api.get<AdminCatalogueResponse>(`/api/admin/catalogue${modelId ? `?modelId=${modelId}` : ''}`),
    // the list on screen stays while another model's loads (and when the page settles on the default one)
    placeholderData: keepPreviousData,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin-catalogue'] });
  qc.invalidateQueries({ queryKey: ['catalogue'] }); // Configurator's read model
}

export function useSaveModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CatalogueModelInput) => api.post<{ id: number }>('/api/admin/catalogue/model', v),
    onSuccess: () => invalidate(qc),
  });
}
export function useSaveCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CatalogueCategoryInput) => api.post<{ id: number }>('/api/admin/catalogue/category', v),
    onSuccess: () => invalidate(qc),
  });
}
export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/admin/catalogue/category-delete', { id }),
    onSuccess: () => invalidate(qc),
  });
}
export function useSaveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CatalogueItemInput) => api.post<{ id: number }>('/api/admin/catalogue/item', v),
    onSuccess: () => invalidate(qc),
  });
}
export function useDiscontinueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post('/api/admin/catalogue/item-delete', { id }),
    onSuccess: () => invalidate(qc),
  });
}
export function useReorderCatalogue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { type: 'category' | 'item'; ids: number[] }) => api.post('/api/admin/catalogue/reorder', v),
    onSuccess: () => invalidate(qc),
  });
}
